/**
 * Brief 80 — PolicyEditor (write surface for MLCO-owned policies)
 *
 * MLCO/Director-only modal that loads the current policy version, lets the
 * user edit body_markdown, requires a free-text reason (audit log invariant),
 * and posts to /api/policy/<slug>/version. On 200, the parent reloads the
 * dashboard.
 *
 * BM-H3980 (8 Oct 2026): opened from a change pack (``changeRequest``), the
 * same editor saves a DRAFT to the pack instead (PUT
 * /api/policy-change-packs/requests/<id>/draft). Nothing goes live until the
 * pack is published. The horizon scan items behind the change sit beside it.
 *
 * 24 Oct 2026 (Vanessa): the sparkle button beside Reason asks Claude (Haiku)
 * for a reason drawn from those items and the diff of the draft. It only
 * fills the box; the owner edits it and nothing saves until they save.
 */

import React, { useEffect, useState } from "react";
import { X, Save, AlertCircle, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { authFetch } from "../../config/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PolicyEditor({ onClose, changeRequest = null, onSaved = null }) {
  const cr = changeRequest;
  const prop = cr?.proposed || {};
  const [slugs, setSlugs] = useState([]);
  const [slug, setSlug] = useState(cr ? cr.policy_slug : "aml-policy");
  const [body, setBody] = useState(cr ? (prop.body_markdown || cr.current_body_markdown || "") : "");
  const [reason, setReason] = useState(cr ? (prop.reason || "") : "");
  const [reviewBy, setReviewBy] = useState(cr ? (prop.review_by || "") : "");
  // BM-H3565: the firm's own numbering. Blank means the next point release.
  const [versionLabel, setVersionLabel] = useState(cr ? (prop.version_label || "") : "");
  const [currentLabel, setCurrentLabel] = useState(cr ? (cr.current_version || "") : "");
  const [loading, setLoading] = useState(!cr);
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (cr) return undefined;   // a change pack hands over the text it holds
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r1 = await authFetch(
          `${API}/api/policy/_mlco_owned_slugs`,
          { credentials: "include" },
        );
        if (r1.ok) {
          const j = await r1.json();
          setSlugs(j.slugs || []);
        }
        const r = await authFetch(
          `${API}/api/policy/${slug}`,
          { credentials: "include" },
        );
        if (r.ok) {
          const j = await r.json();
          setBody(j.version?.body_markdown || "");
          setCurrentLabel(j.version?.version_label || "");
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
    return undefined;
  }, [slug, cr]);

  async function suggestReason() {
    setSuggesting(true);
    setError(null);
    try {
      const r = await authFetch(`${API}/api/policy-change-packs/requests/${cr._id}/suggest-reason`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body_markdown: body }),
      });
      if (!r.ok) {
        let detail = await r.text();
        try { detail = JSON.parse(detail).detail || detail; } catch (_) { /* plain text */ }
        throw new Error(detail);
      }
      const j = await r.json();
      setReason(j.reason || "");
      setSuggested(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSuggesting(false);
    }
  }

  async function submit() {
    if (!reason.trim()) {
      setError("Reason is required (audit log invariant)");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (cr) {
        const r = await authFetch(`${API}/api/policy-change-packs/requests/${cr._id}/draft`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            body_markdown: body,
            reason,
            review_by: reviewBy || null,
            version_label: versionLabel.trim() || null,
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status} — ${await r.text()}`);
        setSuccess("Draft saved to the change pack. It goes live only when the pack is published.");
        if (onSaved) onSaved();
        return;
      }
      const r = await authFetch(`${API}/api/policy/${slug}/version`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          body_markdown: body,
          reason,
          review_by: reviewBy || null,
          version_label: versionLabel.trim() || null,
          colp_signoff_method: "single-colp",
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status} — ${await r.text()}`);
      const j = await r.json();
      const archived = j.archive?.archived
        ? `${currentLabel || "The previous version"} is archived in SharePoint.`
        : `${currentLabel || "The previous version"} is not archived yet: ${j.archive?.reason || "unknown reason"}.`;
      const links = j.links_moved
        ? ` ${j.links_moved} link(s) moved to ${j.version_label}; ${j.links_flagged} need re-verifying because their section changed.`
        : "";
      setSuccess(`Created ${j.version_label}.${links} ${archived} Staff sign-offs to renew: ${j.attestations_invalidated}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const title = cr
    ? `Draft ${[cr.policy_ref, cr.policy_name].filter(Boolean).join(" ")} for the change pack`
    : "Edit a policy";

  return (
    <Modal title={title} onClose={onClose} testid="policy-editor-modal">
      <div className="space-y-3">
        {cr ? (
          <div data-testid="policy-editor-scan-items" className="text-sm border rounded p-2 bg-gray-50">
            <div className="font-medium mb-1">Why this policy is changing (horizon scan)</div>
            <ul className="space-y-1">
              {(cr.scan_items || []).map((s) => (
                <li key={s.id}>
                  <a href={s.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">
                    {s.title} <ExternalLink size={11} />
                  </a>
                  {s.suggestion?.reason ? <span className="text-gray-600"> · {s.suggestion.reason}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div>
            <label className="text-sm font-medium" htmlFor="policy-slug">Policy</label>
            <select
              id="policy-slug"
              data-testid="policy-editor-slug-select"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            >
              {slugs.length === 0 ? (
                <option value="aml-policy">aml-policy</option>
              ) : slugs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-sm font-medium" htmlFor="body">Body (markdown)</label>
          <textarea
            id="body"
            data-testid="policy-editor-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={cr ? 18 : 10}
            className="w-full mt-1 border rounded p-2 font-mono text-sm"
            disabled={loading || submitting}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium" htmlFor="version-label">New version</label>
            <input
              id="version-label"
              data-testid="policy-editor-version-label"
              type="text"
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              placeholder={currentLabel ? `Now ${currentLabel}; leave blank for the next point release` : "v1"}
              className="w-full mt-1 border rounded p-2"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="review-by">Review by (ISO date)</label>
            <input
              id="review-by"
              data-testid="policy-editor-review-by"
              type="date"
              value={reviewBy}
              onChange={(e) => setReviewBy(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            />
          </div>
          <div className={cr ? "sm:col-span-2" : ""}>
            <label className="text-sm font-medium" htmlFor="reason">Reason (required)</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                id="reason"
                data-testid="policy-editor-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={cr ? "What prompted it: what changes, e.g. Axiom Ince: record-keeping controls" : "Why this change"}
                className="w-full border rounded p-2"
              />
              {cr ? (
                <button
                  type="button"
                  data-testid="policy-editor-suggest-reason"
                  onClick={suggestReason}
                  disabled={suggesting || submitting || !body}
                  title="Suggest a reason from the horizon scan items and your changes"
                  aria-label="Suggest a reason"
                  className="p-2 border rounded text-indigo-700 border-indigo-300 hover:bg-indigo-50 disabled:opacity-50"
                >
                  {suggesting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                </button>
              ) : null}
            </div>
            {suggested ? (
              <div data-testid="policy-editor-reason-suggested" className="text-xs text-gray-500 mt-1">
                Suggested by Claude. Check and edit it before you save.
              </div>
            ) : null}
          </div>
        </div>
        {error && (
          <div data-testid="policy-editor-error" className="text-sm text-red-700 flex items-center gap-1">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        {success && (
          <div data-testid="policy-editor-success" className="text-sm text-green-700">
            {success}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="policy-editor-cancel"
            onClick={onClose}
            className="px-3 py-1.5 text-sm border rounded"
            disabled={submitting}
          >
            {success ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            data-testid="policy-editor-submit"
            onClick={submit}
            className="px-3 py-1.5 text-sm border rounded bg-gray-900 text-white inline-flex items-center gap-1 disabled:opacity-50"
            disabled={submitting || !body || !reason.trim()}
          >
            <Save size={14} /> {cr ? "Save draft to the change pack" : "Save new version"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div data-testid={testid} className="bg-white rounded shadow-lg w-full max-w-3xl max-h-full overflow-auto">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
